// Payments: the card-terminal providers (Tyro, ANZ Worldline), the custom
// tenders the POS can record, the store's Stripe Connect account (online
// payments and payouts), and — for platform staff only — the platform fee.
// Receipt options live on POS appearance (the sitemap's split).

import { useCallback, useEffect, useState } from 'react';
import { ExternalLink, Pencil, Trash2, X, Check as Tick } from 'lucide-react';
import { api, ApiError, dateTimeOf, money, type Me } from '../../lib/api';
import { useBase } from '../../scope';
import { Check, errMsg, Head, useAction } from './shared';
import type {
  CustomPayment, CustomPaymentInput, PayoutInterval, PlatformSettings, StripeAccount, StripeAccountLink,
  StripeBalance, StripePayouts, TerminalProvider, TerminalProviderSettings,
} from './types-b';

const PROVIDERS: { which: TerminalProvider; label: string }[] = [
  { which: 'tyro', label: 'Tyro' },
  { which: 'anz_worldline', label: 'ANZ Worldline' },
];

const INTERVALS: PayoutInterval[] = ['daily', 'weekly', 'monthly', 'manual'];

const freshTender = (): CustomPaymentInput => ({ name: '', availablePickup: true, availableDelivery: true, availableDineIn: true, active: true });

const tenderBody = (d: CustomPaymentInput): CustomPaymentInput => ({ ...d, name: d.name.trim() });

const availability = (c: CustomPaymentInput): string => {
  const on = [c.availablePickup && 'pickup', c.availableDelivery && 'delivery', c.availableDineIn && 'dine-in'].filter(Boolean);
  return on.length ? on.join(' · ') : 'no collection type';
};

/** One terminal provider: its own form, its own save. */
function ProviderRow(props: { base: string; which: TerminalProvider; label: string }) {
  const { base, which, label } = props;
  const [s, setS] = useState<TerminalProviderSettings | null>(null);
  const act = useAction();
  useEffect(() => {
    let live = true;
    api.get<TerminalProviderSettings>(`${base}/settings/providers/${which}`).then(
      (v) => { if (live) setS(v); },
      (e) => { if (live) act.setError(errMsg(e)); },
    );
    return () => { live = false; };
  }, [base, which, act.setError]);
  if (!s) return <div className="rowline"><b>{label}</b><span className="loading">Loading…</span></div>;
  return (
    <div className="rowline" data-provider={which} style={{ flexWrap: 'wrap' }}>
      <div style={{ width: 130 }}><b style={{ fontSize: 14 }}>{label}</b></div>
      <label className="checkline">
        <input type="checkbox" aria-label={`${label} enabled`} checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} />
        Enabled
      </label>
      <input className="input" placeholder="Merchant ID" aria-label={`${label} merchant ID`} style={{ maxWidth: 220 }}
        value={s.merchantId ?? ''} onChange={(e) => setS({ ...s, merchantId: e.target.value })} />
      <button className="btn btn-outline btn-sm" disabled={act.busy}
        onClick={() => void act.run(async () => {
          setS(await api.put<TerminalProviderSettings>(`${base}/settings/providers/${which}`, { enabled: s.enabled, merchantId: s.merchantId?.trim() || null }));
        })}>
        {act.busy ? 'Saving…' : `Save ${label}`}
      </button>
      {act.saved && <span className="saved-flash">Saved</span>}
      {act.error && <div className="err" role="alert" style={{ flexBasis: '100%' }}>{act.error}</div>}
    </div>
  );
}

function TenderForm(props: { draft: CustomPaymentInput; onChange: (d: CustomPaymentInput) => void; prefix: string }) {
  const { draft: d, onChange, prefix } = props;
  return (
    <>
      <input className="input" style={{ maxWidth: 200 }} placeholder="Gift voucher" aria-label={`${prefix} tender name`} value={d.name}
        onChange={(e) => onChange({ ...d, name: e.target.value })} />
      <Check label="Pickup" checked={d.availablePickup} onChange={(v) => onChange({ ...d, availablePickup: v })} />
      <Check label="Delivery" checked={d.availableDelivery} onChange={(v) => onChange({ ...d, availableDelivery: v })} />
      <Check label="Dine-in" checked={d.availableDineIn} onChange={(v) => onChange({ ...d, availableDineIn: v })} />
      <Check label="Active" checked={d.active} onChange={(v) => onChange({ ...d, active: v })} />
    </>
  );
}

export function Payments() {
  const base = useBase();

  // ── custom tenders ─────────────────────────────────────────────────
  const [tenders, setTenders] = useState<CustomPayment[] | null>(null);
  const [newTender, setNewTender] = useState<CustomPaymentInput>(freshTender());
  const [editId, setEditId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<CustomPaymentInput>(freshTender());
  const tenderAct = useAction();

  // ── Stripe ─────────────────────────────────────────────────────────
  const [stripe, setStripe] = useState<StripeAccount | 'none' | null>(null);
  const [balance, setBalance] = useState<StripeBalance | null>(null);
  const [payouts, setPayouts] = useState<StripePayouts | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [interval, setPayoutInterval] = useState<PayoutInterval | ''>('');
  const [schedule, setSchedule] = useState<string | null>(null);
  const stripeAct = useAction();
  const scheduleAct = useAction();

  // ── platform fee (super only) ──────────────────────────────────────
  const [fee, setFee] = useState<PlatformSettings | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const loadTenders = useCallback(async () => {
    setTenders(await api.get<CustomPayment[]>(`${base}/custom-payments`));
  }, [base]);

  const loadStripe = useCallback(async () => {
    try {
      const a = await api.get<StripeAccount>(`${base}/stripe/account`);
      setStripe(a);
      const [b, p] = await Promise.all([
        api.get<StripeBalance>(`${base}/stripe/balance`),
        api.get<StripePayouts>(`${base}/stripe/payouts`),
      ]);
      setBalance(b);
      setPayouts(p);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setStripe('none');
      else throw e;
    }
  }, [base]);

  useEffect(() => {
    void (async () => {
      try {
        await Promise.all([loadTenders(), loadStripe()]);
        const me = await api.get<Me>('/me');
        if (me.isSuperAdmin) setFee(await api.get<PlatformSettings>('/platform/settings'));
        setLoadErr(null);
      } catch (e) {
        setLoadErr(errMsg(e));
      }
    })();
  }, [loadTenders, loadStripe]);

  const tenderRun = (fn: () => Promise<unknown>) => void tenderAct.run(async () => { await fn(); await loadTenders(); });

  return (
    <>
      <Head title="Payments" />
      {loadErr && <div className="err" role="alert">{loadErr}</div>}
      <div className="set-cols">
        <div className="set-stack">
          <div className="panel" data-section="providers">
            <h2>Card terminals</h2>
            <div className="hint" style={{ margin: '6px 0 8px' }}>
              The integrated EFTPOS providers the POS can take card payments through. An enabled provider needs its merchant ID.
            </div>
            {PROVIDERS.map((p) => <ProviderRow key={p.which} base={base} which={p.which} label={p.label} />)}
          </div>

          <div className="panel" data-section="custom-payments">
            <h2>Custom payment types</h2>
            <div className="hint" style={{ margin: '6px 0 8px' }}>
              Vouchers, bank transfer, house accounts — tenders the POS can record besides cash and card.
            </div>
            {tenders === null && <div className="loading">Loading…</div>}
            {tenders?.length === 0 && <div className="loading">No custom payment types.</div>}
            {tenders?.map((t) => (
              <div key={t.customPaymentId} className="rowline" style={{ flexWrap: 'wrap' }}>
                {editId === t.customPaymentId ? (
                  <>
                    <TenderForm draft={editDraft} onChange={setEditDraft} prefix="Edit" />
                    <button className="btn btn-primary btn-sm" disabled={tenderAct.busy || !editDraft.name.trim()}
                      onClick={() => tenderRun(async () => {
                        await api.put<CustomPayment>(`${base}/custom-payments/${t.customPaymentId}`, tenderBody(editDraft));
                        setEditId(null);
                      })}>
                      <Tick size={14} /> Save tender
                    </button>
                    <button className="btn btn-ghost btn-sm" title="Cancel edit" onClick={() => setEditId(null)}><X size={14} /></button>
                  </>
                ) : (
                  <>
                    <div className="grow">
                      <b>{t.name}</b>
                      <span className="sub">{availability(t)}{t.active ? '' : ' · inactive'}</span>
                    </div>
                    <button className="btn btn-ghost btn-sm" title={`Edit ${t.name}`} disabled={tenderAct.busy}
                      onClick={() => { setEditId(t.customPaymentId); setEditDraft({ name: t.name, availablePickup: t.availablePickup, availableDelivery: t.availableDelivery, availableDineIn: t.availableDineIn, active: t.active }); }}>
                      <Pencil size={14} />
                    </button>
                    <button className="btn btn-ghost btn-sm" title={`Delete ${t.name}`} disabled={tenderAct.busy}
                      onClick={() => { if (confirm(`Delete the payment type “${t.name}”?`)) tenderRun(() => api.del(`${base}/custom-payments/${t.customPaymentId}`)); }}>
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>
            ))}
            <div className="rowline" style={{ flexWrap: 'wrap', marginTop: 8 }}>
              <TenderForm draft={newTender} onChange={setNewTender} prefix="New" />
              <button className="btn btn-outline btn-sm" disabled={tenderAct.busy || !newTender.name.trim()}
                onClick={() => tenderRun(async () => {
                  await api.post<CustomPayment>(`${base}/custom-payments`, tenderBody(newTender));
                  setNewTender(freshTender());
                })}>
                Add tender
              </button>
            </div>
            {tenderAct.error && <div className="err" role="alert">{tenderAct.error}</div>}
          </div>
        </div>

        <div className="set-stack">
          <div className="panel" data-section="stripe">
            <h2>Online payments — Stripe</h2>
            {stripe === null && <div className="loading">Loading…</div>}
            {stripe === 'none' && (
              <div className="set-stack" style={{ paddingTop: 8, gap: 12 }}>
                <div className="kv"><b>Not connected.</b> <span className="hint">Set up a Stripe account for this store to take card payments online and receive payouts.</span></div>
                <div>
                  <button className="btn btn-primary" disabled={stripeAct.busy}
                    onClick={() => void stripeAct.run(async () => { await api.post<StripeAccount>(`${base}/stripe/account`); await loadStripe(); })}>
                    {stripeAct.busy ? 'Working…' : 'Set up Stripe'}
                  </button>
                </div>
              </div>
            )}
            {stripe !== null && stripe !== 'none' && (
              <div className="set-stack" style={{ paddingTop: 8, gap: 14 }}>
                <div className="rowline">
                  <div className="grow">
                    <b data-account={stripe.connectedAccountId}>{stripe.connectedAccountId}</b>
                    <span className="sub">{stripe.detailsSubmitted ? 'Details submitted' : 'Onboarding not finished'}</span>
                  </div>
                  <span className={`chip ${stripe.chargesEnabled ? 'chip-paid' : 'chip-unpaid'}`}>charges {stripe.chargesEnabled ? 'on' : 'off'}</span>
                  <span className={`chip ${stripe.payoutsEnabled ? 'chip-paid' : 'chip-unpaid'}`}>payouts {stripe.payoutsEnabled ? 'on' : 'off'}</span>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button className="btn btn-outline btn-sm" disabled={stripeAct.busy}
                    onClick={() => void stripeAct.run(async () => { setLink((await api.post<StripeAccountLink>(`${base}/stripe/account/links`)).url); })}>
                    {stripe.detailsSubmitted ? 'Update details on Stripe' : 'Continue onboarding'}
                  </button>
                  {link && (
                    <a className="btn btn-primary btn-sm" href={link} target="_blank" rel="noreferrer" data-onboarding-link>
                      Open Stripe onboarding <ExternalLink size={13} />
                    </a>
                  )}
                </div>
                <div data-section="stripe-balance">
                  <div className="overline" style={{ marginBottom: 6 }}>Balance</div>
                  {balance === null ? <div className="loading">Loading…</div> : (
                    <div className="kv">
                      Available <b data-stat="available">{balance.available.map((a) => `${money(a.amount)} ${a.currency.toUpperCase()}`).join(' + ') || money(0)}</b>
                      {' · '}pending <b data-stat="pending">{balance.pending.map((a) => `${money(a.amount)} ${a.currency.toUpperCase()}`).join(' + ') || money(0)}</b>
                    </div>
                  )}
                </div>
                <div data-section="stripe-payouts">
                  <div className="overline" style={{ marginBottom: 6 }}>Payouts</div>
                  {payouts?.data.length === 0 && <div className="loading">No payouts yet.</div>}
                  {payouts?.data.map((p) => (
                    <div key={p.id} className="rowline">
                      <div className="grow"><b>{money(p.amount)}</b><span className="sub">{p.status}{p.arrival_date ? ` · arrives ${dateTimeOf(new Date(p.arrival_date * 1000).toISOString())}` : ''}</span></div>
                    </div>
                  ))}
                  <div className="frow" style={{ marginTop: 10 }}>
                    <div className="field narrow" style={{ flexBasis: 160 }}>
                      <label htmlFor="payout-interval">Payout schedule</label>
                      <select id="payout-interval" className="input" aria-label="Payout interval" value={interval} onChange={(e) => setPayoutInterval(e.target.value as PayoutInterval)}>
                        <option value="">Choose…</option>
                        {INTERVALS.map((i) => <option key={i} value={i}>{i}</option>)}
                      </select>
                    </div>
                    <button className="btn btn-outline btn-sm" disabled={scheduleAct.busy || !interval}
                      onClick={() => void scheduleAct.run(async () => {
                        const acct = await api.put<{ settings?: { payouts?: { schedule?: { interval?: string } } } }>(`${base}/stripe/payout-schedule`, { interval });
                        setSchedule(acct.settings?.payouts?.schedule?.interval ?? interval);
                      })}>
                      Set schedule
                    </button>
                    {scheduleAct.saved && <span className="saved-flash">Saved</span>}
                  </div>
                  {schedule && <div className="hint" style={{ marginTop: 6 }}>Stripe reports the schedule as <b>{schedule}</b>.</div>}
                  {scheduleAct.error && <div className="err" role="alert">{scheduleAct.error}</div>}
                </div>
              </div>
            )}
            {stripeAct.error && <div className="err" role="alert">{stripeAct.error}</div>}
          </div>

          {fee && (
            <div className="panel" data-section="platform-fee">
              <h2>Platform fee</h2>
              <div className="kv" style={{ paddingTop: 8 }}>
                Restoplus takes <b data-stat="platform-fee">{(fee.platformFeeBp / 100).toFixed(2)}%</b> ({fee.platformFeeBp} bp) of online card payments.
              </div>
              <div className="hint" style={{ marginTop: 6 }}>Platform staff only. Change it under Platform · Settings.</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
